import {
  Checkbox,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ParticipationRequestStatus,
  type BuyerParticipationRequestDto,
  type CandidateLimitsDto,
  type SourcingEventV2Dto,
  useConfirmInvitationMutation,
  Regulation,
} from "../../../../autogen/bff-api";
import { isApiErrorDto } from "../../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { ThemedDateTimePicker } from "../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { minTenderDays, tenderDeadlineProvision } from "../../../sourcing-events/buyer/wizard/utils/karens";
import { citesFoaProvisions } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { urls } from "../../../../urls";
import type { CandidateSelection } from "./SelectionPanel";
import { RecipientGroups } from "./RecipientGroups";

const SUGGESTED_TENDER_DAYS = 30;
const END_OF_DAY = { hours: 23, minutes: 59 };

interface Props {
  event: SourcingEventV2Dto;
  requests: BuyerParticipationRequestDto[];
  selection: CandidateSelection;
  limits: CandidateLimitsDto | null;
}

export const SendInvitationCard = ({ event, requests, selection, limits }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDateWithTime = useDisplayDateWithTime();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmInvitation, { isLoading }] = useConfirmInvitationMutation();
  const apiError = useApiError();

  const sendInvitationPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const minDays = minTenderDays(
    sendInvitationPublicCtx?.regulation,
    sendInvitationPublicCtx?.foaPart,
    sendInvitationPublicCtx?.procedureType,
  );
  const [deadline, setDeadline] = useState<string>(
    moment().add(SUGGESTED_TENDER_DAYS, "days").set({ hour: 12, minute: 0, second: 0 }).toISOString(),
  );
  const [validityMonths, setValidityMonths] = useState<number>(
    sendInvitationPublicCtx?.eFormsNoticeData?.tenderValidityMonths ?? 3,
  );
  const [serverBelowMinimum, setServerBelowMinimum] = useState(false);

  useEffect(() => {
    setServerBelowMinimum(false);
  }, [deadline]);

  const qualified = requests.filter((r) => r.status === ParticipationRequestStatus.Qualified);
  const unresolved = requests.filter((r) => r.status === ParticipationRequestStatus.Submitted);
  const rejected = requests.filter((r) => r.status === ParticipationRequestStatus.Rejected);

  const supplierName = (r: BuyerParticipationRequestDto) => r.organizationName ?? r.organizationId;

  const invitedRequests =
    limits != null ? qualified.filter((r) => selection.selectedIds.includes(r.id)) : qualified;
  const notSelectedRequests =
    limits != null ? qualified.filter((r) => !selection.selectedIds.includes(r.id)) : [];

  const invited = invitedRequests.map((r) => ({ name: supplierName(r) }));
  const notSelected = notSelectedRequests.map((r) => ({
    name: supplierName(r),
    reason: selection.notSelected.find((ns) => ns.requestId === r.id)?.reason ?? null,
  }));
  const rejectedEntries = rejected.map((r) => ({
    name: supplierName(r),
    reason: r.rejectionReason ?? null,
  }));

  // § 24-10 legacy guard: any REJECTED request with a blank rejectionReason is a hard block.
  const missingRejectionReasons = rejected.filter((r) => !r.rejectionReason?.trim());
  const hasLegacyBlock = missingRejectionReasons.length > 0;

  // Forsyningsforskriften § 16-3 (2): the buyer may instead agree the tender deadline with every
  // invited supplier — the same deadline for all — and the ten-day floor then does not bind. The
  // platform cannot see that agreement, so the buyer asserts it and the check stands down.
  const isUtilities = sendInvitationPublicCtx?.regulation === Regulation.Forsyningsforskriften;
  const [deadlineAgreed, setDeadlineAgreed] = useState(
    event.eventData.tenderDeadlineAgreedWithCandidates === true,
  );
  const provisionLabel = tenderDeadlineProvision(
    sendInvitationPublicCtx?.regulation,
    sendInvitationPublicCtx?.foaPart,
    sendInvitationPublicCtx?.procedureType,
  );
  // An agreed deadline still has to be in the future — § 16-3 (2) lifts the floor, not the deadline.
  const floorDays = isUtilities && deadlineAgreed ? 0 : minDays;
  const clientBelowMinimum = floorDays != null && moment(deadline).isBefore(moment().add(floorDays, "days"));
  const belowMinimum = clientBelowMinimum || serverBelowMinimum;

  const canSend =
    unresolved.length === 0 &&
    qualified.length > 0 &&
    selection.isValid &&
    !belowMinimum &&
    !hasLegacyBlock;

  // Status line when prerequisites are unmet
  const statusLine = (() => {
    if (unresolved.length > 0)
      return t("{{count}} requests are still missing a review.", { count: unresolved.length });
    if (qualified.length === 0)
      return t("No qualified suppliers — consider cancelling the competition.");
    if (!selection.isValid)
      return t("Complete the selection above before sending.");
    return null;
  })();

  const TENDER_DEADLINE_BELOW_MINIMUM = "zz_SourcingEventApiErrorCode_TenderDeadlineBelowMinimum";

  const handleConfirm = async () => {
    try {
      await confirmInvitation({
        eventId: event.id,
        confirmInvitationRequestDto: {
          bidSubmissionDeadline: deadline,
          tenderValidityMonths: validityMonths,
          // § 16-3 (2): the server runs its own floor check, so the assertion travels with the
          // confirmation rather than only convincing the client.
          ...(isUtilities && { tenderDeadlineAgreedWithCandidates: deadlineAgreed }),
          ...(limits != null && {
            selectedRequestIds: selection.selectedIds,
            notSelected: selection.notSelected,
          }),
        },
      }).unwrap();
      onClose();
    } catch (e) {
      if (
        isFetchBaseQueryError(e) &&
        isApiErrorDto(e.data) &&
        e.data.errorCode === TENDER_DEADLINE_BELOW_MINIMUM
      ) {
        setServerBelowMinimum(true);
        onClose();
        return;
      }
      apiError.trigger(e);
    }
  };

  // Dialog body: omit zero-count parts
  const dialogParts: string[] = [];
  if (invited.length > 0) {
    dialogParts.push(
      t("{{count}} invited with bid deadline {{deadline}}", {
        count: invited.length,
        deadline: displayDateWithTime(deadline),
      }),
    );
  }
  if (notSelected.length > 0) {
    dialogParts.push(
      t("{{count}} get your selection reason", { count: notSelected.length }),
    );
  }
  if (rejectedEntries.length > 0) {
    dialogParts.push(
      t("{{count}} get the rejection", { count: rejectedEntries.length }),
    );
  }
  const total = invited.length + notSelected.length + rejectedEntries.length;

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
      <CardBody>
        {/* tabIndex={-1}: focus target for the qualification stepper's scroll-to-section */}
        <Heading as="h3" size="sm" color="smText" mb="1" tabIndex={-1}>
          {t("Send the invitation")}
        </Heading>
        <Text fontSize="sm" color="smMuted" mb="5">
          {/*
            The duty to answer everyone at once holds under both regulations; only FOA's paragraph
            numbers are cited, because forsyningsforskriften's counterparts are not verified here.
            Two whole literals rather than one interpolated string: langen only extracts static
            t("…") calls, so a conditional inside t() drops both keys from the locales.
          */}
          {isUtilities
            ? t(
                "Everyone gets their answer at the same moment: the invited simultaneously and in writing, the others their message without delay.",
              )
            : t(
                "Everyone gets their answer at the same moment: the invited simultaneously and in writing (FOA § 23-2), the others their message without delay (FOA §§ 16-12 (4), 24-10).",
              )}
        </Text>

        <Flex gap="6" wrap="wrap" mb="5">
          <FormControl flex="1" minW="220px">
            <FormLabel>{t("Bid deadline")}</FormLabel>
            <ThemedDateTimePicker
              id="sendCardBidDeadline"
              date={deadline}
              onChange={(iso) => {
                if (iso) {
                  setDeadline(iso);
                  setServerBelowMinimum(false);
                }
              }}
              defaultTime={END_OF_DAY}
            />
            <FormHelperText>
              {floorDays != null && floorDays > 0 && minDays != null && provisionLabel != null
                ? t("Minimum {{minDays}} days from the invitation ({{provision}}).", {
                    minDays,
                    provision: provisionLabel,
                  })
                : t("The deadline must leave the invited suppliers time to tender.")}
            </FormHelperText>
            {isUtilities && (
              <Checkbox
                mt="2"
                size="sm"
                isChecked={deadlineAgreed}
                onChange={(e) => {
                  setDeadlineAgreed(e.target.checked);
                  setServerBelowMinimum(false);
                }}
              >
                <Text fontSize="sm">
                  {t("The deadline is agreed with every invited supplier (forsyningsforskriften § 16-3 (2))")}
                </Text>
              </Checkbox>
            )}
            {belowMinimum && (
              <Text mt="1" fontSize="sm" color="smError">
                {t("Pick a later date before confirming.")}
              </Text>
            )}
          </FormControl>

          <FormControl flex="1" minW="180px">
            <FormLabel>{t("Tender validity period")}</FormLabel>
            <NumberInput
              value={validityMonths}
              min={1}
              max={36}
              onChange={(_, value) => {
                if (!Number.isNaN(value)) setValidityMonths(value);
              }}
              width="28"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              {t("How long the suppliers are bound by their tenders (months).")}
            </FormHelperText>
          </FormControl>
        </Flex>

        <RecipientGroups
          citesFoa={citesFoaProvisions(sendInvitationPublicCtx?.regulation)}
          invited={invited}
          notSelected={notSelected}
          rejected={rejectedEntries}
        />

        {hasLegacyBlock && (
          <Box mt="4">
            {missingRejectionReasons.map((r) => (
              <Alert key={r.id} status="warning" rounded="md" mb="2">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {t(
                    "{{name}} is missing the reason for rejection (required, FOA § 24-10). The invitation cannot be sent until it is in place.",
                    { name: supplierName(r) },
                  )}{" "}
                  <Button
                    variant="link"
                    size="sm"
                    colorScheme="orange"
                    onClick={() =>
                      navigate(
                        urls.v2.sourcingEvents.participationRequestDetail.go(event.id, r.id),
                      )
                    }
                  >
                    {t("Go to the review")}
                  </Button>
                </AlertDescription>
              </Alert>
            ))}
          </Box>
        )}

        {statusLine && (
          <Text mt="4" fontSize="sm" color="smError">
            {statusLine}
          </Text>
        )}

        <Flex justify="flex-end" mt="5">
          <Button colorScheme="teal" onClick={onOpen} isDisabled={!canSend}>
            {t("Send the invitation")}
          </Button>
        </Flex>

        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t("Send the invitation?")}
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text fontSize="sm">
                  {t("{{total}} suppliers are notified now: {{parts}}. The list is locked — changes require cancelling the competition.", {
                    total,
                    parts: dialogParts.join(", "),
                  })}
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose} variant="ghost">
                  {t("Cancel")}
                </Button>
                <Button
                  colorScheme="teal"
                  onClick={handleConfirm}
                  isLoading={isLoading}
                  isDisabled={!canSend}
                  ml={3}
                >
                  {t("Send")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </CardBody>
    </Card>
  );
};
